/**
 * Catálogo de notificações push configuráveis pelo usuário.
 *
 * Fonte única de verdade para os tipos de notificação, a audiência (papel que
 * pode recebê-la) e o estado padrão (ligado/desligado). Espelhado em:
 *  - Cloud Functions (`apps/functions/src/lib/webPush.ts`) — fan-out filtra por
 *    `AUDIENCE_BY_KIND` (gate de papel, hard-coded) **e** pela preferência do
 *    membro (`resolvePrefs`).
 *  - Web (`apps/app`) — `NotificationPrefsCard` lista os toggles via
 *    `notificationKindsForRole`.
 *  - Android (`feature/profile`) — replica o catálogo num `object` Kotlin
 *    (não há fonte cross-language no repo).
 *
 * A preferência é armazenada por-tenant em `tenants/{tid}/members/{uid}.
 * notificationPrefs` (ver `MemberSchema`). Campo ausente → default aplicado no
 * read via `resolveNotificationPrefs`.
 */
import type { TenantRole } from './enums';

/**
 * Tipos de notificação, na ordem em que aparecem no Perfil.
 *
 * - `labelConsumed` — etiqueta baixada (consumida).
 * - `labelDiscarded` — etiqueta descartada (push consolidado do
 *   `flushDiscardNotifications`). Ficou fora do catálogo até 2026-09-05: o
 *   push saía por `sendWebPushToOwners`, sem passar pela preferência do
 *   membro nem pelo gate de tenant morto.
 * - `labelExpired` — etiqueta venceu (transição automática para EXPIRED).
 * - `labelExpiring24h` / `labelExpiring48h` — etiqueta entrando na janela.
 * - `memberChanges` — usuário/colaborador/responsável criado ou removido.
 * - `printerChanges` — impressora criada/alterada/removida.
 * - `bulkPrint` — impressão em massa (lote acima do limiar).
 * - `stockLow` — saldo de um produto caiu para baixo/zerado.
 * - `billing` — falha de pagamento, suspensão, renovação ou reembolso (MBG).
 * - `expiryDigest` — resumo diário matinal de vencimentos.
 * - `checklistNcCritical` — item crítico de checklist concluído como não
 *   conformidade (#336, F5). Só NC de item crítico avisa; NC comum não.
 * - `checklistOverdue` — checklist programado passou do prazo sem ser
 *   concluído (#336, F5).
 */
export const NOTIFICATION_KINDS = [
  'labelConsumed',
  'labelDiscarded',
  'labelExpired',
  'labelExpiring24h',
  'labelExpiring48h',
  'memberChanges',
  'printerChanges',
  'bulkPrint',
  'stockLow',
  'billing',
  'expiryDigest',
  'checklistNcCritical',
  'checklistOverdue',
] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * Audiência de cada notificação — quem pode recebê-la, por papel.
 *
 * - `ALL` — qualquer membro (OWNER/MANAGER/STAFF).
 * - `OWNER_MANAGER` — OWNER e MANAGER.
 * - `OWNER` — só OWNER.
 *
 * É um gate **hard-coded**: o fan-out nunca envia um tipo OWNER-only a um
 * não-OWNER, mesmo que a preferência esteja ligada (defesa em profundidade).
 */
export type NotificationAudience = 'ALL' | 'OWNER_MANAGER' | 'OWNER';

export const AUDIENCE_BY_KIND: Record<NotificationKind, NotificationAudience> = {
  labelConsumed: 'ALL',
  // OWNER-only desde sempre (o push nascia de `sendWebPushToOwners`);
  // entrar no catálogo não é o momento de ampliar a audiência.
  labelDiscarded: 'OWNER',
  labelExpired: 'ALL',
  labelExpiring24h: 'ALL',
  labelExpiring48h: 'ALL',
  memberChanges: 'OWNER',
  printerChanges: 'OWNER',
  bulkPrint: 'OWNER',
  stockLow: 'OWNER_MANAGER',
  billing: 'OWNER',
  expiryDigest: 'ALL',
  // #336 F5: aviso de gestão — STAFF executa o checklist, mas nunca recebe.
  checklistNcCritical: 'OWNER_MANAGER',
  checklistOverdue: 'OWNER_MANAGER',
};

/**
 * Estado padrão de cada notificação quando o membro nunca configurou.
 *
 * Todas ligadas por padrão, exceto `bulkPrint` e `expiryDigest` (ruidosas, opt-in).
 */
export const NOTIFICATION_DEFAULTS: Record<NotificationKind, boolean> = {
  labelConsumed: true,
  labelDiscarded: true,
  labelExpired: true,
  labelExpiring24h: true,
  labelExpiring48h: true,
  memberChanges: true,
  printerChanges: true,
  bulkPrint: false,
  stockLow: true,
  billing: true,
  expiryDigest: false,
  checklistNcCritical: true,
  checklistOverdue: true,
};

/** Preferências do usuário: subconjunto de kinds → ligado/desligado. */
export type NotificationPrefs = Partial<Record<NotificationKind, boolean>>;

/** `true` se o papel está na audiência da notificação. */
export function roleInAudience(
  role: TenantRole | null | undefined,
  audience: NotificationAudience,
): boolean {
  if (!role) return false;
  switch (audience) {
    case 'ALL':
      return true;
    case 'OWNER_MANAGER':
      return role === 'OWNER' || role === 'MANAGER';
    case 'OWNER':
      return role === 'OWNER';
  }
}

/** Default de um kind quando o membro não tem preferência salva. */
export function defaultNotificationPref(kind: NotificationKind): boolean {
  return NOTIFICATION_DEFAULTS[kind];
}

/**
 * Kinds que um papel pode configurar/receber, na ordem do catálogo.
 * Usado pela UI para listar só os toggles relevantes ao papel do membro.
 */
export function notificationKindsForRole(
  role: TenantRole | null | undefined,
): NotificationKind[] {
  return NOTIFICATION_KINDS.filter((kind) =>
    roleInAudience(role, AUDIENCE_BY_KIND[kind]),
  );
}

/**
 * Resolve as preferências de um membro aplicando os defaults aos kinds ausentes.
 * Server e cliente usam isto no read — não há backfill no Firestore.
 */
export function resolveNotificationPrefs(
  prefs: NotificationPrefs | null | undefined,
): Record<NotificationKind, boolean> {
  return { ...NOTIFICATION_DEFAULTS, ...(prefs ?? {}) };
}

/**
 * `true` se o membro deve receber a notificação: papel na audiência **e**
 * preferência não desligada (ausente conta como default).
 */
export function shouldReceiveNotification(
  role: TenantRole | null | undefined,
  kind: NotificationKind,
  prefs: NotificationPrefs | null | undefined,
): boolean {
  if (!roleInAudience(role, AUDIENCE_BY_KIND[kind])) return false;
  return resolveNotificationPrefs(prefs)[kind];
}
